import JSZip from 'jszip';

// ═══════════════════════════════════════════════════════════════
// Čtení záhlaví a zápatí z OOXML (Fáze 2, doplnění importu).
//
// Mammoth záhlaví/zápatí úplně ignoruje (převádí jen tělo dokumentu), proto
// je čteme sami z `word/header*.xml` / `word/footer*.xml`. Vazbu „která
// sekce používá který header/footer" určuje `word/document.xml` přes
// <w:headerReference r:id> / <w:footerReference>, ty se překládají přes
// `word/_rels/document.xml.rels` na konkrétní soubor.
//
// Bereme „default" typ (ne first/even) — Fáze 2 nepodporuje různá záhlaví
// pro sudé/liché ani první stranu. Převod je záměrně jednoduchý: odstavce
// (w:p), runy (w:r/w:t) s tučným/kurzívou/podtržením a zarovnáním, plus
// pole PAGE / NUMPAGES → naše <span data-field>. Bohatší formátování (barvy,
// fonty) se u záhlaví ztrácí — přijatelný kompromis Fáze 2.
// ═══════════════════════════════════════════════════════════════

export interface HeaderFooterHtml {
  headerHtml: string;
  footerHtml: string;
}

export async function extractHeaderFooter(buffer: ArrayBuffer): Promise<HeaderFooterHtml> {
  const empty = { headerHtml: '', footerHtml: '' };
  let zip: JSZip;
  try {
    zip = await JSZip.loadAsync(buffer);
  } catch {
    return empty;
  }

  const docXml = await readEntry(zip, 'word/document.xml');
  const relsXml = await readEntry(zip, 'word/_rels/document.xml.rels');
  if (!docXml || !relsXml) return empty;

  const rels = parseRels(relsXml);

  const headerId = firstRefId(docXml, 'w:headerReference');
  const footerId = firstRefId(docXml, 'w:footerReference');

  const headerHtml = headerId && rels[headerId] ? await convertPart(zip, 'word/' + rels[headerId]) : '';
  const footerHtml = footerId && rels[footerId] ? await convertPart(zip, 'word/' + rels[footerId]) : '';

  return { headerHtml, footerHtml };
}

async function readEntry(zip: JSZip, path: string): Promise<string | null> {
  const f = zip.file(path);
  if (!f) return null;
  try {
    return await f.async('string');
  } catch {
    return null;
  }
}

/** rId → cílový soubor (Target) z rels. */
function parseRels(xml: string): Record<string, string> {
  const map: Record<string, string> = {};
  const re = /<Relationship\b[^>]*\bId="([^"]+)"[^>]*\bTarget="([^"]+)"/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(xml)) !== null) {
    map[m[1]] = m[2].replace(/^\/?word\//, '');
  }
  return map;
}

/** Vrátí r:id první reference daného typu (přednostně default). */
function firstRefId(docXml: string, tag: string): string | null {
  // Preferuj type="default"; jinak vezmi první výskyt.
  const def = docXml.match(new RegExp(`<${tag}\\b[^>]*w:type="default"[^>]*r:id="([^"]+)"`));
  if (def) return def[1];
  const any = docXml.match(new RegExp(`<${tag}\\b[^>]*r:id="([^"]+)"`));
  return any ? any[1] : null;
}

/** Převede header/footer XML na jednoduché HTML pro panel. */
async function convertPart(zip: JSZip, path: string): Promise<string> {
  const xml = await readEntry(zip, path);
  if (!xml) return '';

  const paras: string[] = [];
  const pRe = /<w:p\b[\s\S]*?<\/w:p>/g;
  let pm: RegExpExecArray | null;
  while ((pm = pRe.exec(xml)) !== null) {
    paras.push(convertParagraph(pm[0]));
  }
  const html = paras.filter((p) => p !== null).join('');
  return html || '';
}

function convertParagraph(p: string): string {
  // Zarovnání z <w:jc w:val="center|right|both">
  const jc = p.match(/<w:jc\s+w:val="(\w+)"/);
  let align = '';
  if (jc) {
    const v = jc[1];
    if (v === 'center') align = ' style="text-align:center"';
    else if (v === 'right') align = ' style="text-align:right"';
    else if (v === 'both' || v === 'justify') align = ' style="text-align:justify"';
  }

  // Projdeme obsah odstavce v pořadí a poskládáme runy i pole (PAGE/NUMPAGES)
  // tak, jak jdou za sebou. Pole mohou být zapsaná dvěma způsoby:
  //   • <w:fldSimple w:instr="… PAGE …">…</w:fldSimple>
  //   • rozložené: <w:instrText>PAGE</w:instrText> mezi fldChar begin/end
  // Sesbíráme runy a instrText tokeny v pořadí výskytu podle jejich pozice.
  interface Piece { pos: number; html: string }
  const pieces: Piece[] = [];

  // Runy s textem
  const rRe = /<w:r\b[\s\S]*?<\/w:r>/g;
  let rm: RegExpExecArray | null;
  while ((rm = rRe.exec(p)) !== null) {
    const runXml = rm[0];
    // instrText uvnitř runu = pole
    const instr = runXml.match(/<w:instrText[^>]*>([\s\S]*?)<\/w:instrText>/);
    if (instr) {
      const field = fieldFromInstr(instr[1]);
      if (field) { pieces.push({ pos: rm.index, html: field }); continue; }
    }
    const html = convertRun(runXml);
    if (html) pieces.push({ pos: rm.index, html });
  }

  // fldSimple pole
  const fsRe = /<w:fldSimple\b[^>]*w:instr="([^"]*)"[^>]*>/g;
  let fm: RegExpExecArray | null;
  while ((fm = fsRe.exec(p)) !== null) {
    const field = fieldFromInstr(fm[1]);
    if (field) pieces.push({ pos: fm.index, html: field });
  }

  pieces.sort((a, b) => a.pos - b.pos);
  const inner = pieces.map((x) => x.html).join('');

  return `<p${align}>${inner || '<br>'}</p>`;
}

/** Z instrukce pole (např. " PAGE \* MERGEFORMAT ") udělá náš token. */
function fieldFromInstr(instr: string): string | null {
  if (/\bNUMPAGES\b/.test(instr)) return '<span data-field="pages">##</span>';
  if (/\bPAGE\b/.test(instr)) return '<span data-field="page">#</span>';
  return null;
}

function convertRun(r: string): string {
  // Text z <w:t>…</w:t> (může být víc)
  let text = '';
  const tRe = /<w:t\b[^>]*>([\s\S]*?)<\/w:t>/g;
  let tm: RegExpExecArray | null;
  while ((tm = tRe.exec(r)) !== null) {
    text += tm[1];
  }
  if (!text) return '';
  text = unescapeXml(text);

  const bold = /<w:b\b(?:\s|\/|>)/.test(r) && !/<w:b\b[^>]*w:val="(?:false|0)"/.test(r);
  const italic = /<w:i\b(?:\s|\/|>)/.test(r) && !/<w:i\b[^>]*w:val="(?:false|0)"/.test(r);
  const underline = /<w:u\b/.test(r) && !/<w:u\b[^>]*w:val="none"/.test(r);

  let out = escapeHtml(text);
  if (bold) out = `<strong>${out}</strong>`;
  if (italic) out = `<em>${out}</em>`;
  if (underline) out = `<u>${out}</u>`;
  return out;
}

function unescapeXml(s: string): string {
  return s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&');
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
